// Single source of truth for who may open a merchant-portal page.
// Used by the sidebar (show/hide link) and ProtectedRoute (block typed URLs), so they can't drift.
// Pages with a permission use hasCapability (role fallback applies until capabilities load);
// pages without one are role-only. UI-only — the backend is the real enforcement.
export interface AccessCtx {
  role: string | null;
  hasCapability: (name: string, fallbackRoles?: string | string[]) => boolean;
}

const ROUTE_ACCESS = {
  // any dashboard widget the user can see (Dash.tsx redirects itself when none)
  "/dashboard": ({ hasCapability: h }) =>
    h("TRANSACTION:REPORT:EXPORT", ["MERCHANT", "CASHIER", "FINANCE"]) ||
    h("TERMINAL:LIVE_STATUS:READ", "MERCHANT") ||
    h("TERMINAL:CASHIER_TERMINAL:READ", "CASHIER"),
  "/transactions": ({ hasCapability: h }) => h("TRANSACTION:LIST", ["MERCHANT", "CASHIER", "FINANCE"]),
  "/terminals": ({ hasCapability: h }) => h("TERMINAL:LIST", ["MERCHANT", "TERMINAL_MANAGER"]),
  "/terminal": ({ hasCapability: h }) => h("TERMINAL:CASHIER_TERMINAL:READ", "CASHIER"), // cashier's own terminal
  "/reconciliation": ({ hasCapability: h }) => h("TRANSACTION:RECONCILIATION:READ", ["MERCHANT", "FINANCE"]),
  "/refunds": ({ hasCapability: h }) => h("TRANSACTION:REFUND:LIST", ["MERCHANT", "CASHIER", "FINANCE"]),
  // no permission exists for these yet — role-only
  "/discounts": ({ role }) => role === "MERCHANT" || role === "FINANCE",
  "/settlement": ({ role }) => role === "MERCHANT" || role === "FINANCE",
  "/management": ({ role }) => role === "MERCHANT",
  "/bulk": ({ role }) => role === "MERCHANT", // reached from Management (bulk-uploads staff)
  "/settings": () => true,
} satisfies Record<string, (a: AccessCtx) => boolean>;

export type GuardedRoute = keyof typeof ROUTE_ACCESS;

export const canAccess = (path: GuardedRoute, ctx: AccessCtx) => ROUTE_ACCESS[path](ctx);

// Same priority as the sidebar; /settings is open to everyone so this always resolves.
// Dash.tsx passes skip="/dashboard" so it can't redirect to itself.
const LANDING_ORDER: GuardedRoute[] = ["/dashboard", "/transactions", "/terminals", "/reconciliation", "/refunds", "/settings"];
export const firstAccessiblePath = (ctx: AccessCtx, skip?: GuardedRoute) =>
  LANDING_ORDER.find((p) => p !== skip && canAccess(p, ctx))!;
